/**
 * Lot ORG du chantier 0.5.x (vague 4 ; registre n° 1 et 14) — fiches d'organisation.
 *
 * - Plateforme : `GET /admin/organisations/{id}` (`PlatformOrganisationsController#fiche`) —
 *   identité, statut, palier, compteurs, administrateurs enrôlés ; noms des projets et
 *   programmes pour SA propre organisation seulement (besoin d'en connaître : une organisation
 *   tierce se lit par l'accès exceptionnel, ARB-SEC-04).
 * - Organisation courante (`OrganisationFicheController`) : `GET/PATCH /org-profile`,
 *   `GET /org-waivers[/{id}]` (dérogations de toute l'organisation), `GET /org-connectors`
 *   (inventaire sans aucun secret).
 */
import { request } from './http'
import type { RiskLevel, Waiver, WaiverStatus } from './types/organisation'

export interface IdentiteOrganisation {
  organisation_id: string
  name: string
  description: string | null
  status: 'active' | 'suspended' | string
  created_at: string | null
  updated_at: string | null
  updated_by: string | null
  plan_code: string
  status_changed_at: string | null
  status_changed_by: string | null
  status_reason: string | null
  missions_policy: string | null
}

export interface CompteursOrganisation {
  projects: number
  projects_archived: number
  programs: number
  members: number
  members_active: number
  project_owners: number
  live_missions: number
  tokens_month: number
  ai_calls_month: number
  active_exceptional_access: number
  pending_enrolments: number
}

export interface AdministrateurEnrole {
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  enrolled_at: string | null
  activated_at: string | null
  enrolment_status: 'activated' | 'pending' | 'expired' | 'revoked'
}

export interface FichePlateformeOrganisation {
  organisation: IdentiteOrganisation
  counters: CompteursOrganisation
  administrators: AdministrateurEnrole[]
  own_organisation: boolean
  projects_visibility: 'own_organisation' | 'exceptional_access_required'
  projects: { workspace_id: string; name: string; status: string; program_id: string | null; created_at: string | null }[] | null
  programs: { program_id: string; name: string; status: string }[] | null
}

export interface ProfilOrganisation {
  organisation: IdentiteOrganisation
  counters: CompteursOrganisation
  administrators: AdministrateurEnrole[]
  can_edit: boolean
  limits: { name_min: number; name_max: number; description_max: number }
}

export interface DerogationOrganisation {
  id: number
  workspace_id: string
  workspace_name: string
  workspace_status: string | null
  scope_requirement_id: number
  requirement_code: string
  category: string
  risk_level: RiskLevel
  owner: string
  requested_by: string
  requested_at: string
  expires_at: string
  status: WaiverStatus
  effective_status: WaiverStatus
  days_to_expiry: number
  statement: string
}

export interface DerogationOrganisationDetail extends DerogationOrganisation {
  justification?: string
  compensating_controls?: string[]
  approval_route?: Waiver['approval_route']
  reviewed_by?: string | null
  reviewed_at?: string | null
  review_verdict?: string | null
  review_comment?: string | null
  decided_by?: string | null
  decided_at?: string | null
  decision_comment?: string | null
  lifted_by?: string | null
  lifted_at?: string | null
  member: boolean
}

export interface DerogationsOrganisation {
  scope: 'organisation' | 'member_projects'
  items: DerogationOrganisation[]
  by_status: Record<string, number>
  projects: number
  hidden_waivers: number
}

export interface ConnecteurOrganisation {
  kind: 'ai_provider' | 'gitlab' | 'odoo'
  id: string
  name: string
  provider: string
  endpoint: string
  workspace_id: string | null
  workspace_name: string | null
  enabled: boolean
  secret_configured: boolean
  secret_ref: string | null
  health: string | null
  health_at: string | null
  created_by: string | null
  created_at: string | null
  updated_at: string | null
}

export interface InventaireConnecteurs {
  connectors: ConnecteurOrganisation[]
  by_kind: Record<string, number>
  secrets_shown: false
}

export interface ModificationProfil {
  name?: string
  description?: string | null
}

const enc = encodeURIComponent

/** Clés de cache partagées (barre de contexte, fiche, édition). */
export const CLE_PROFIL_ORGANISATION = ['organisation', 'profil'] as const
export const CLE_FICHE_PLATEFORME = (organisationId: string) => ['plateforme', 'organisations', 'fiche', organisationId] as const

export const organisationFicheApi = {
  fichePlateforme: (organisationId: string) =>
    request<FichePlateformeOrganisation>(`/admin/organisations/${enc(organisationId)}`),
  profil: () => request<ProfilOrganisation>('/org-profile'),
  modifierProfil: (m: ModificationProfil) =>
    request<IdentiteOrganisation>('/org-profile', { method: 'PATCH', body: JSON.stringify(m) }),
  derogations: () => request<DerogationsOrganisation>('/org-waivers'),
  derogation: (id: string | number) => request<DerogationOrganisationDetail>(`/org-waivers/${enc(String(id))}`),
  connecteurs: () => request<InventaireConnecteurs>('/org-connectors'),
}
